import { XIcon } from 'lucide-react'
import { type FC, memo, type ReactNode, useCallback } from 'react'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { ChatAttachment } from './chat-input-attachment.interface'
import { IMAGE_PREVIEW_HEIGHT } from './chat-input-attachment.interface'

export interface ChatInputImagePreviewProps {
  attachment: ChatAttachment

  onRemove: (id: string) => void

  className?: string
}

const ChatInputImagePreviewBase: FC<ChatInputImagePreviewProps> = ({
  attachment,
  onRemove,
  className,
}): ReactNode => {
  const handleRemove = useCallback(() => {
    onRemove(attachment.id)
  }, [attachment.id, onRemove])

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'chat-input-image-preview group relative shrink-0 overflow-hidden rounded-lg border bg-muted',
            'transition-shadow hover:ring-2 hover:ring-ring/50',
            className
          )}
          style={{ height: IMAGE_PREVIEW_HEIGHT }}
        >
          <img
            src={attachment.previewUrl}
            alt={attachment.name}
            className="h-full w-auto object-cover"
            style={{ minWidth: IMAGE_PREVIEW_HEIGHT }}
          />
          <Button
            type="button"
            variant="secondary"
            size="icon"
            onClick={handleRemove}
            className={cn(
              'chat-input-image-remove absolute right-1 bottom-1 size-6 rounded-full opacity-0 shadow-md',
              'transition-opacity group-hover:opacity-100',
              'hover:bg-destructive hover:text-destructive-foreground'
            )}
            aria-label={`Remove ${attachment.name}`}
          >
            <XIcon className="size-3" />
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top">{attachment.name}</TooltipContent>
    </Tooltip>
  )
}

export const ChatInputImagePreview = memo(ChatInputImagePreviewBase)

ChatInputImagePreview.displayName = 'ChatInputImagePreview'
